/**
 * Константы для API и сетевых запросов
 * Централизованное хранение всех API-связанных магических значений
 */

import { TIME_INTERVALS } from "./constants";

/**
 * Базовые URL для API.
 * Для клиентского кода (браузер, Tauri) используйте apiUrl из @/shared/api/config.
 * SERVER_DEV/SERVER_PROD оба берут env API_URL; для разведения окружений используйте NODE_ENV или отдельные env.
 */
export const API_URLS = {
  CLIENT_BASE: (process.env["NEXT_PUBLIC_API_URL"] as string) || "/api",
  SERVER_DEV: (process.env["API_URL"] as string) || "http://backend-dev:8080",
  SERVER_PROD: (process.env["API_URL"] as string) || "http://backend:80",
} as const;

/**
 * Таймауты запросов в миллисекундах
 */
export const API_TIMEOUTS = {
  /** Стандартный таймаут */
  DEFAULT: 30000, // 30 секунд
  /**
   * Читающие запросы (GET/HEAD), если вызывающий не задал свой (KAN-595).
   * Прод-замер за неделю: p99 всех ручек, кроме `admin/statistics`, укладывается
   * в 6 секунд, а Traefik всё равно рвёт соединение своим 30-секундным
   * таймаутом — ждать дольше нечего.
   */
  READ: 20000, // 20 секунд
  /**
   * Мутации (POST/PUT/PATCH/DELETE), если вызывающий не задал свой (KAN-595).
   * Больше, чем у чтения: `Tournaments/register/solo` и `Auth/register-email`
   * на хвосте дают p99 около 28 секунд, и резать их клиентским таймаутом —
   * значит ломать регистрацию тем, кто и так ждёт дольше всех.
   */
  WRITE: 30000, // 30 секунд
  /** Быстрый таймаут */
  FAST: 5000, // 5 секунд
  /** Медленный таймаут */
  SLOW: 60000, // 1 минута
  /** Таймаут для загрузки файлов */
  UPLOAD: 300000, // 5 минут
} as const;

/**
 * Коды ответов API
 */
export const API_STATUS_CODES = {
  /** Успешный ответ */
  SUCCESS: 200,
  /** Создан */
  CREATED: 201,
  /** Нет содержимого */
  NO_CONTENT: 204,
  /** Плохой запрос */
  BAD_REQUEST: 400,
  /** Не авторизован */
  UNAUTHORIZED: 401,
  /** Запрещено */
  FORBIDDEN: 403,
  /** Не найдено */
  NOT_FOUND: 404,
  /** Конфликт */
  CONFLICT: 409,
  /** Необрабатываемая сущность */
  UNPROCESSABLE_ENTITY: 422,
  /** Слишком большой файл */
  PAYLOAD_TOO_LARGE: 413,
  /** Неподдерживаемый тип медиа */
  UNSUPPORTED_MEDIA_TYPE: 415,
  /** Внутренняя ошибка сервера */
  INTERNAL_SERVER_ERROR: 500,
  /** Сервис недоступен */
  SERVICE_UNAVAILABLE: 503,
} as const;

/**
 * Конфигурация React Query
 */
export const REACT_QUERY_CONFIG = {
  /** Время устаревания данных по умолчанию */
  DEFAULT_STALE_TIME: 5 * TIME_INTERVALS.MINUTE, // 5 минут
  /** Время сборки мусора по умолчанию */
  DEFAULT_GC_TIME: 10 * TIME_INTERVALS.MINUTE, // 10 минут
  /** Количество повторных попыток */
  DEFAULT_RETRY: 1,
  /** Максимальное количество повторных попыток */
  MAX_RETRY: 3,
  /** Интервал между повторными попытками */
  RETRY_DELAY: 1000, // 1 секунда
} as const;

/**
 * Классы волатильности данных (KAN-1013): пара staleTime + gcTime.
 *
 * Раньше по коду жили десятки магических 30_000/60_000: одни и те же данные
 * (например, команды турнира) в разных местах устаревали через разное время.
 *
 * Важно про этот проект: глобально `refetchOnMount: false` и
 * `refetchOnWindowFocus: false`. Поэтому при повторном открытии страницы запрос
 * уходит не по staleTime, а когда данные вытеснены из кэша по gcTime (по умолчанию
 * 10 минут) — для справочников решает именно gcTime. staleTime же решает, обновит ли
 * данные инвалидация/реконнект и запросы с `refetchOnMount: true`.
 *
 * - REFERENCE — справочники: каталог чемпионов, фиче-флаги. Меняются патчем или
 *   действием админа; держим в памяти сутки, свежими считаем час.
 * - STANDARD — обычные данные, меняющиеся по действиям людей: турнир, составы,
 *   агрегаты статистики.
 * - LIVE — идёт прямо сейчас: результаты, расписание матчей идущего турнира.
 *   ponytail: 15 с вместо «0 + инвалидация по push» — push-инвалидация ждёт KAN-983;
 *   когда она будет, LIVE.staleTime станет 0.
 */
export const QUERY_VOLATILITY = {
  REFERENCE: { staleTime: TIME_INTERVALS.HOUR, gcTime: 24 * TIME_INTERVALS.HOUR },
  STANDARD: { staleTime: 5 * TIME_INTERVALS.MINUTE, gcTime: 10 * TIME_INTERVALS.MINUTE },
  // Глобальный refetchOnMount: false показал бы при повторном заходе старый результат
  // идущего матча — живым данным он нужен.
  LIVE: {
    staleTime: 15 * TIME_INTERVALS.SECOND,
    gcTime: 5 * TIME_INTERVALS.MINUTE,
    refetchOnMount: true,
  },
} as const;

/**
 * Специфичные настройки для разных типов запросов
 */
export const QUERY_SPECIFIC_CONFIG = {
  /** Пользовательские данные */
  USER: {
    staleTime: TIME_INTERVALS.MINUTE, // 1 минута
    gcTime: 30 * TIME_INTERVALS.MINUTE, // 30 минут
  },
  /** Список турниров */
  TOURNAMENT_LIST: {
    staleTime: 2 * TIME_INTERVALS.MINUTE, // 2 минуты
    gcTime: 10 * TIME_INTERVALS.MINUTE, // 10 минут
  },
  /** Детали турнира */
  TOURNAMENT_DETAIL: {
    staleTime: 5 * TIME_INTERVALS.MINUTE, // 5 минут
    gcTime: 15 * TIME_INTERVALS.MINUTE, // 15 минут
  },
  /** Список организаций */
  ORGANIZATION_LIST: {
    staleTime: 5 * TIME_INTERVALS.MINUTE, // 5 минут
    gcTime: 20 * TIME_INTERVALS.MINUTE, // 20 минут
  },
  /** Детали организации (одна по id/url) */
  ORGANIZATION_DETAIL: {
    staleTime: 5 * TIME_INTERVALS.MINUTE,
    gcTime: 30 * TIME_INTERVALS.MINUTE,
  },
  /** Профиль пользователя по username (чужой профиль) */
  PROFILE_BY_USERNAME: {
    staleTime: 2 * TIME_INTERVALS.MINUTE,
    gcTime: 15 * TIME_INTERVALS.MINUTE,
  },
  /** Стримы */
  STREAMS: {
    staleTime: 30 * TIME_INTERVALS.SECOND, // 30 секунд
    gcTime: 5 * TIME_INTERVALS.MINUTE, // 5 минут
  },
  /** Уведомления */
  NOTIFICATIONS: {
    staleTime: 30 * TIME_INTERVALS.SECOND, // 30 секунд
    gcTime: 5 * TIME_INTERVALS.MINUTE, // 5 минут
  },
  /** Счетчик непрочитанных уведомлений */
  UNREAD_COUNT: {
    staleTime: 15 * TIME_INTERVALS.SECOND, // 15 секунд
    gcTime: 2 * TIME_INTERVALS.MINUTE, // 2 минуты
  },
  /** Настройки уведомлений */
  NOTIFICATION_SETTINGS: {
    staleTime: TIME_INTERVALS.MINUTE, // 1 минута
    gcTime: 10 * TIME_INTERVALS.MINUTE, // 10 минут
  },
} as const;

/**
 * Настройки для мутаций
 */
export const MUTATION_CONFIG = {
  /** Количество повторных попыток для мутаций */
  RETRY: 1,
  /** Режим сети для мутаций */
  NETWORK_MODE: "offlineFirst" as const,
} as const;

/**
 * Константы для аутентификации
 */
export const AUTH_CONSTANTS = {
  /** Префикс для dev токенов */
  DEV_TOKEN_PREFIX: "dev_token_",
  /** Префикс для refresh токенов */
  REFRESH_TOKEN_PREFIX: "refresh_",
  /** Время жизни токена по умолчанию */
  TOKEN_LIFETIME: 50 * TIME_INTERVALS.MINUTE, // 50 минут
  /** Время жизни refresh токена */
  REFRESH_TOKEN_LIFETIME: 7 * TIME_INTERVALS.DAY, // 7 дней
  /** Задержка перед инициализацией auth */
  AUTH_INIT_DELAY: 0,
  /** Задержка после сохранения токенов */
  TOKEN_SAVE_DELAY: 100, // 100ms
  /** Таймаут запроса /me в auth-потоке (инициализация, refetch после OAuth) */
  ME_REQUEST_TIMEOUT_MS: 10_000,
  /** Таймаут запроса POST /auth/oauth/callback. Бэкенд теперь ходит в Discord
   *  direct-first (~0.3s), но даём запас на медленную сеть/прокси-фолбэк. */
  OAUTH_CALLBACK_TIMEOUT_MS: 30_000,
} as const;

/**
 * Константы для Twitch API
 */
export const TWITCH_CONSTANTS = {
  /** Время жизни токена Twitch */
  TOKEN_LIFETIME: 50 * TIME_INTERVALS.MINUTE, // 50 минут
  /** Интервал обновления стримов */
  STREAM_UPDATE_INTERVAL: 30 * TIME_INTERVALS.SECOND, // 30 секунд
  /** Дебаунс для поиска каналов */
  CHANNEL_SEARCH_DEBOUNCE: 500, // 500ms
} as const;

/**
 * Константы для обработки ошибок
 */
export const ERROR_HANDLING = {
  /** Максимальная длина сообщения об ошибке */
  MAX_ERROR_MESSAGE_LENGTH: 500,
  /** Максимальная глубина стека ошибки */
  MAX_STACK_DEPTH: 10,
  /** Время показа критических ошибок */
  CRITICAL_ERROR_DURATION: 10 * TIME_INTERVALS.SECOND, // 10 секунд
  /** Время показа обычных ошибок */
  NORMAL_ERROR_DURATION: 5 * TIME_INTERVALS.SECOND, // 5 секунд
} as const;

/**
 * Валидирует только критически важные API константы
 */
function validateCriticalApiConstants(): void {
  // Валидация таймаутов (критично для UX)
  if (API_TIMEOUTS.DEFAULT > 60000) {
    throw new Error("DEFAULT_TIMEOUT не должен превышать 60 секунд");
  }
  if (API_TIMEOUTS.UPLOAD > 600000) {
    throw new Error("UPLOAD_TIMEOUT не должен превышать 10 минут");
  }

  // Валидация HTTP кодов (критично для обработки ошибок)
  const criticalCodes = [
    API_STATUS_CODES.UNAUTHORIZED,
    API_STATUS_CODES.NOT_FOUND,
    API_STATUS_CODES.INTERNAL_SERVER_ERROR,
  ];
  criticalCodes.forEach((code) => {
    if (code < 100 || code >= 600) {
      throw new Error(`Критический HTTP код ${code} невалиден`);
    }
  });
}

// Запускаем валидацию только критических констант
validateCriticalApiConstants();
